import React, { useState, useEffect, useRef, useContext } from "react";
import {
  Box, Typography, Avatar, CircularProgress,
  Button, IconButton,
  Chip,
  LinearProgress,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";

import Marb from "../../assests/images/marseg.svg";
import MarlboroRed from "../../assests/images/marlboro_red.svg";
import marlboroGold from "../../assests/images/marlboro_gold.svg";
import MarlboroGoldAdvance from "../../assests/images/marlboro-gold-advance.svg";
import marlboroFuseBeyond from "../../assests/images/marlboro-fuse-beyond.svg";
import MarlborocloveMix from "../../assests/images/marlboro-clove-mix.svg";
import MarlboroVistaForestFusion from "../../assests/images/marlboro-vista-forest-fusion.svg";
import { useNavigate } from "react-router-dom";
import { getDailySurveyData, createDailySurvey } from "../../utils/apis";
import { AuthContext } from "../../context/AuthContext";
import Icons from "../Icons";
import TopIcon from "../../assests/images/topIcon.svg";

const variantImages: Record<string, string> = {
  "Marlboro Red": MarlboroRed,
  "Marlboro Gold": marlboroGold,
  "Marlboro Gold Advance": MarlboroGoldAdvance,
  "Marlboro Fuse Beyond": marlboroFuseBeyond,
  "Marlboro Clove Mix": MarlborocloveMix,
  "Marlboro Vista Forest Fusion": MarlboroVistaForestFusion,
};

interface Variant {
  id: number;
  name: string;
  group?: string;
  img_path?: string;
  group_id?: number;
  packcount?: number;
}

type Props = {
  accountId?: string | null;
};

// ─── ALL SUB-COMPONENTS OUTSIDE ───────────────────────────────────────────────

const NumInput = React.memo(({
  name,
  values,
  setValues,
}: {
  name: string;
  values: Record<string, string>;
  setValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}) => {
  return (
    <input
      value={values[name] ?? ""}
       placeholder="0"
      onChange={(e) => {
        const val = e.target.value;
        if (/^\d*$/.test(val)) {
          setValues((prev) => ({ ...prev, [name]: val }));
        }
      }}
      inputMode="numeric"
      maxLength={3}
      pattern="[0-9]*"
      style={{
        width: "40px",
        height: "40px",
        textAlign: "center",
        fontSize: "16px",
        fontWeight: 400,
        borderRadius: "8px",
        border: "1px solid #e0e0e0",
        outline: "none",
        background: "#f9f9f9",
        caretColor: "#C8172F",
      }}
    />
  );
});

const VariantCard = React.memo(({
  variant,
  values,
  setValues,
  sx = {},
}: {
  variant: Variant;
  values: Record<string, string>;
  setValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  sx?: object;
}) => {
  const image = variant.img_path || variantImages[variant.name] || Marb;
  return (
    <Box sx={{
      backgroundColor: "#fff", borderRadius: 2,
      boxShadow: "0px 1px 4px rgba(0,0,0,0.07)",
      p: 1.5, display: "flex", alignItems: "center", gap: 1.5, ...sx,
    }}>
      <Avatar sx={{ width: 52, height: 24, borderRadius: 0, background: "transparent" }}>
        <img src={image} style={{ width: "100%" }} alt={variant.name} />
      </Avatar>
      <Typography fontWeight={600} sx={{ fontSize: "14px", flex: 1, textAlign: "left", lineHeight: "1rem" }}>
        {variant.name}
      </Typography>
      <NumInput name={variant.name} values={values} setValues={setValues} />
    </Box>
  );
});

const GroupedVariantList = React.memo(({
  groupedData,
  values,
  setValues,
  openGroup,
  setOpenGroup,
}: {
  groupedData: any[];
  values: Record<string, string>;
  setValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  openGroup: string | null;
  setOpenGroup: React.Dispatch<React.SetStateAction<string | null>>;
}) => (
  <>
    {groupedData.map((item) => {
      if (item.type === "group") {
        return (
          <Box
            key={item.name}
            sx={{
              mb: 1.25,
              background: "#F5F3F0",
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: "0 0 6px 3px #efeaea",
            }}
          >
            <Box
              onClick={() => setOpenGroup(openGroup === item.name ? null : item.name)}
              sx={{
                backgroundColor: "#fff",
                p: 1.5,
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid #e8e3e3",
              }}
            >
              <Box sx={{ textAlign: "left" }}>
                <Typography fontWeight={700} sx={{ fontSize: "14px" }}>
                  {item.name}
                </Typography>
                <Typography sx={{ fontSize: "11px", color: "#6C6C6C" }}>
                  {item.items.map((i: Variant) => i.name).join(" + ")}
                </Typography>
              </Box>
              <KeyboardArrowDownIcon
                sx={{
                  fontSize: 22,
                  color: "#333",
                  transform: openGroup === item.name ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "0.3s",
                }}
              />
            </Box>

            {openGroup === item.name && (
              <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: "12px" }}>
                {item.items.map((v: Variant) => (
                  <VariantCard
                    key={v.id}
                    variant={v}
                    values={values}
                    setValues={setValues}
                  />
                ))}
              </Box>
            )}
          </Box>
        );
      }

      return (
        <VariantCard
          key={item.id}
          variant={item}
          values={values}
          setValues={setValues}
          sx={{ mb: 1.25 }}
        />
      );
    })}
  </>
));

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

const SelfAuditFlow: React.FC<Props> = ({ accountId }) => {
  const authContext = useContext(AuthContext);

  const getContactIdFromToken = (): string | null => {
    try {
      const token = localStorage.getItem("token") || authContext?.token || "";
      if (!token) return null;
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload?.User?.ContactId || null;
    } catch (err) {
      console.error("Failed to parse token for contactId:", err);
      return null;
    }
  };

  const resolvedAccountId = accountId ?? getContactIdFromToken();
  const today = new Date();

  const isMonthLocked = (viewDate: Date): boolean => {
    const todayDay = today.getDate();
    const todayMonth = today.getMonth();
    const todayYear = today.getFullYear();
    const vm = viewDate.getMonth();
    const vy = viewDate.getFullYear();
    if (vy === todayYear && vm === todayMonth) return false;
    const prevMonth = todayMonth === 0 ? 11 : todayMonth - 1;
    const prevYear = todayMonth === 0 ? todayYear - 1 : todayYear;
    if (vy === prevYear && vm === prevMonth) return todayDay > 7;
    return true;
  };

  const [variants, setVariants] = useState<Variant[]>([]);
  const [topTaskId, setTopTaskId] = useState<number | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [currentDate, setCurrentDate] = useState(today);
  const [selectedDate, setSelectedDate] = useState(today.toDateString());
  const [totalDisplay, setTotalDisplay] = useState<string>("");
  const pendingEntriesRef = useRef<Record<string, Record<string, string>>>({});
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [daysLeft, setDaysLeft] = useState(0);
  const navigate = useNavigate();

  const scrollToIndex = (index: number, delay: number = 100) => {
    const itemWidth = 60;
    setTimeout(() => {
      containerRef.current?.scrollTo({
        left: Math.max(0, index * itemWidth - itemWidth * 2),
        behavior: "smooth",
      });
    }, delay);
  };

  const selectedDateRef = useRef(selectedDate);
  useEffect(() => {
    selectedDateRef.current = selectedDate;
  }, [selectedDate]);

  const parseApiData = (json: any[], overrideValues?: Record<string, string>) => {
    const apiData = json[0];
    if (apiData.id) setTopTaskId(apiData.id);
    if (apiData["total_actual_vol/total_target_vol"] !== undefined) {
      setTotalDisplay(apiData["total_actual_vol/total_target_vol"] || "");
    }
setDaysLeft(apiData.days_left || 0);
    const formatted: Variant[] = [];
    apiData.clubs?.forEach((club: any) => {
      club.variants?.forEach((v: any) => {
        formatted.push({
          id: v.variant_id,
          name: v.name,
          group: club.club_name,
          img_path: v.img_path || "",
          group_id: club.club_id,
          packcount: v.packcount,
        });
      });
    });
    apiData.standalone_variants?.forEach((v: any) => {
      formatted.push({ id: v.variant_id, name: v.name, packcount: v.packcount, img_path: v.img_path || "" });
    });

    setVariants(formatted);
    if (overrideValues) {
      setValues(overrideValues);
    } else {
      const init: Record<string, string> = {};
      formatted.forEach((v) => { init[v.name] = String(v.packcount || ""); });
      setValues(init);
    }
  };

  // Fires when month changes — clears all pending accumulated entries
  useEffect(() => {
    if (!resolvedAccountId) { setLoading(false); return; }
    pendingEntriesRef.current = {};
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token") || "";
        const monthStr = currentDate.toLocaleDateString("en-US", { month: "short" });
        const yearStr = String(currentDate.getFullYear());
        const day = new Date(selectedDateRef.current).getDate();
        const res = await getDailySurveyData(token, monthStr, yearStr, day, resolvedAccountId);
        const json = res?.data ?? res;
        if (!json || !Array.isArray(json) || json.length === 0) {
          setVariants([]); setTopTaskId(null); setValues({}); setTotalDisplay("");
          return;
        }
        parseApiData(json);
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [resolvedAccountId, currentDate]);

  // Fires when selected day changes — restores any pending values for that day
  useEffect(() => {
    if (!resolvedAccountId) return;
    const fetchForDate = async () => {
      try {
        const token = localStorage.getItem("token") || "";
        const monthStr = currentDate.toLocaleDateString("en-US", { month: "short" });
        const yearStr = String(currentDate.getFullYear());
        const day = new Date(selectedDate).getDate();
        const res = await getDailySurveyData(token, monthStr, yearStr, day, resolvedAccountId);
        const json = res?.data ?? res;
        if (!json || !Array.isArray(json) || json.length === 0) {
          setVariants([]); setTopTaskId(null); setValues({}); setTotalDisplay("");
          return;
        }
        const pending = pendingEntriesRef.current[selectedDate];
        parseApiData(json, pending);
      } catch (err) {
        console.error("Error fetching for date:", err);
      }
    };
    fetchForDate();
  }, [selectedDate]);

  const getDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const totalDays = new Date(year, month + 1, 0).getDate();
    return Array.from({ length: totalDays }, (_, i) => {
      const d = new Date(year, month, i + 1);
      return {
        fullDate: d,
        day: d.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase(),
        date: i + 1,
      };
    });
  };

  const days = getDays();

  useEffect(() => {
    const now = new Date();
    const isCurrentMonth =
      now.getMonth() === currentDate.getMonth() &&
      now.getFullYear() === currentDate.getFullYear();
    if (isCurrentMonth) {
      setSelectedDate(now.toDateString());
      scrollToIndex(now.getDate() - 1, 300);
    } else {
      const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      setSelectedDate(firstDay.toDateString());
      scrollToIndex(0, 100);
    }
  }, [currentDate]);

  useEffect(() => {
    const now = new Date();
    scrollToIndex(now.getDate() - 1, 500);
  }, []);

  const changeMonth = (step: number) => {
    const d = new Date(currentDate);
    d.setMonth(currentDate.getMonth() + step);
    const now = new Date();
    if (
      d.getFullYear() > now.getFullYear() ||
      (d.getFullYear() === now.getFullYear() && d.getMonth() > now.getMonth())
    ) return;
    setCurrentDate(d);
  };

  const changeYear = (step: number) => {
    const d = new Date(currentDate);
    d.setFullYear(currentDate.getFullYear() + step);
    if (d.getFullYear() > new Date().getFullYear()) return;
    setCurrentDate(d);
  };

  // ─── BUILD GROUPED DATA ───────────────────────────────────
  const singleItems: any[] = [];
  const groupMap: Record<string, any> = {};
  const groupOrder: string[] = [];

  variants.forEach((v) => {
    if (v.group) {
      if (!groupMap[v.group]) {
        groupMap[v.group] = { type: "group", name: v.group, items: [] };
        groupOrder.push(v.group);
      }
      groupMap[v.group].items.push(v);
    } else {
      singleItems.push({ type: "single", ...v });
    }
  });

  const groupedData: any[] = [
    ...singleItems,
    ...groupOrder.map((g) => groupMap[g]),
  ];
const now = new Date();
  // Submits all accumulated dates in one API call
  const handleSubmit = async () => {
    try {
      const allEntries = { ...pendingEntriesRef.current, [selectedDate]: { ...values } };
      const payload: any[] = [];
      for (const [dateStr, dateValues] of Object.entries(allEntries)) {
        const formattedDate = new Date(dateStr).toLocaleDateString("en-CA");
        variants.forEach((v) => {
          const count = Number(dateValues[v.name] || 0);
          if (count > 0) {
            payload.push({
              top_task_id: topTaskId,
              variant_id: v.id,
              club_id: v.group_id || null,
              entry_date: formattedDate,
              pack_count: count,
            });
          }
        });
      }
      const isCurrentMonth =
  currentDate.getMonth() === now.getMonth() &&
  currentDate.getFullYear() === now.getFullYear();
   if (isCurrentMonth && totalEarned === 0 && payload.length === 0) {
        alert("Please fill purchase for this month");
        return;
      }
      await createDailySurvey(payload);
      pendingEntriesRef.current = {};
      navigate("/top-monthly-overview");
    } catch (error) {
      console.error(error);
      alert("Failed to submit survey");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const isLocked = isMonthLocked(currentDate);

  // Parse totalDisplay once for the orange box and progress bar
  const [earnedStr, targetStr] = (totalDisplay || "").split("/");
  const totalEarned = Number(earnedStr) || 0;
  const totalTarget = Number(targetStr) || 0;
  const totalPercent = totalTarget ? (totalEarned / totalTarget) * 100 : 0;
  let pointsColor = "#E53935";
  if (totalPercent > 0 && totalPercent < 100) pointsColor = "#FB8C00";
  if (totalPercent >= 100) pointsColor = "#43A047";

  return (
    <Box sx={{ background: "#FAFAFA", marginBottom: "100px" }}>
      <Box sx={{ background: "linear-gradient(360deg, rgb(249 249 149) 0%, rgb(255 255 243) 100%)", p: 2 }}>
        <Box sx={{ width: "100%", marginBottom: "30px", marginTop: "20px" }}>
          <Typography variant="h6" sx={{ fontWeight: 500, width: "100%", textAlign: "left", fontSize: "16px" }}>
            Top Outlet Program
          </Typography>
        </Box>

        {/* TOP icon + title + chip */}
        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ flexWrap: "nowrap" }}>
          <Box display="flex" alignItems="center" gap={1.2} sx={{ flexShrink: 0 }}>
            <Box sx={{ background: "#ffced8", width: "40px", height: "40px", borderRadius: "30px", padding: "8px" }}>
              <img src={TopIcon} alt="scan Icon" style={{ width: "24px" }} />
            </Box>
            <Box>
              <Typography fontSize={13} color="#757575" sx={{ textAlign: "left" }}>TOP</Typography>
              <Typography fontWeight={600} fontSize={14} textAlign="left">Self Tracking</Typography>
            </Box>
          </Box>
          <Chip
            icon={<Icons name="timerImage" />}
            label={`${daysLeft} Days Left`}
            size="small"
            sx={{ bgcolor: "#f5f5f5", color: "#000", fontWeight: 500, borderRadius: "8px", fontSize: 12, height: 32, flexShrink: 0, ml: 1, mr: 1 }}
          />
        </Box>

        {/* POINTS DISPLAY */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mt: 1, mb: 2, mr: 1 }}>
          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 1,
            background: "linear-gradient(180deg, #FFF4E5 0%, #FFF4E5 100%)",
            borderRadius: "12px", px: 2, py: 1, position: "relative", width: "fit-content", overflow: "hidden",
            "&::before": { content: '""', position: "absolute", top: 0, left: "10%", height: "2px", background: "linear-gradient(to right, #fff5ee 0%, #ff9800 50%, #fff5ee 100%)", width: "80%", borderTopLeftRadius: "12px", borderTopRightRadius: "12px" },
            "&::after": { content: '""', position: "absolute", bottom: 0, left: "10%", height: "2px", background: "linear-gradient(to right, #fff5ee 0%, #ff9800 50%, #fff5ee 100%)", width: "80%", borderBottomLeftRadius: "12px", borderBottomRightRadius: "12px" },
          }}>
            <Typography fontWeight={600} display="flex" alignItems="center" sx={{ zIndex: 1, fontSize: 14 }}>
              <Box component="span" sx={{ color: pointsColor, paddingRight: "3px" }}>{totalEarned}</Box>
              <Box component="span" sx={{ color: "#000" }}>/ {totalTarget}</Box>
            </Typography>
            <Box sx={{ zIndex: 1, lineHeight: 0, fontSize: "12px", fontWeight: "600" }}>Packs</Box>
          </Box>
        </Box>

        {/* PROGRESS BAR */}
        <Box mt={1.5} mb={3} mr={1}>
          <LinearProgress
            variant="determinate"
            value={totalPercent > 100 ? 100 : totalPercent}
            sx={{ height: 4, borderRadius: 5, backgroundColor: "#E0E0E0", "& .MuiLinearProgress-bar": { backgroundColor: "#4CAF50" } }}
          />
        </Box>

        {/* MONTH / YEAR NAV */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Box>
            <IconButton onClick={() => changeYear(-1)} sx={{ width: "34px", height: "34px", backgroundColor: "#fff !important", marginLeft: "10px", border: "1px solid #d4d3d3" }}>
              <KeyboardDoubleArrowLeftIcon />
            </IconButton>
            <IconButton onClick={() => changeMonth(-1)} sx={{ width: "34px", height: "34px", backgroundColor: "#fff !important", marginLeft: "10px", border: "1px solid #d4d3d3" }}>
              <ChevronLeftIcon />
            </IconButton>
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: "16px" }}>
            {currentDate.toLocaleDateString("en-US", { month: "long" })}{" "}
            <span style={{ color: "#c5c5c5" }}>{currentDate.getFullYear()}</span>
          </Typography>
          <Box>
            <IconButton
              onClick={() => changeMonth(1)}
              disabled={currentDate.getMonth() === today.getMonth() && currentDate.getFullYear() === today.getFullYear()}
              sx={{ width: "34px", height: "34px", backgroundColor: "#fff !important", marginRight: "10px", border: "1px solid #d4d3d3" }}
            >
              <ChevronRightIcon />
            </IconButton>
            <IconButton
              onClick={() => changeYear(1)}
              disabled={currentDate.getFullYear() === today.getFullYear()}
              sx={{ width: "34px", height: "34px", backgroundColor: "#fff !important", border: "1px solid #d4d3d3" }}
            >
              <KeyboardDoubleArrowRightIcon />
            </IconButton>
          </Box>
        </Box>

        {/* DAY STRIP */}
        <Box
          ref={containerRef}
          sx={{
            display: "flex", gap: 1, overflowX: "auto", pb: 1,
            "&::-webkit-scrollbar": { display: "none" },
            msOverflowStyle: "none", scrollbarWidth: "none",
          }}
        >
          {days.map((item) => {
            const now = new Date();
            now.setHours(0, 0, 0, 0);
            const isFutureDay = item.fullDate > now;
            const isActive = item.fullDate.toDateString() === selectedDate;
            return (
              <Box
                key={item.fullDate.toString()}
                onClick={() => {
                  if (isFutureDay) return;
                  // Save current values before switching date
                  pendingEntriesRef.current[selectedDate] = { ...values };
                  setSelectedDate(item.fullDate.toDateString());
                  scrollToIndex(item.date - 1, 50);
                }}
                sx={{
                  minWidth: 52, textAlign: "center", p: 1,
                  borderRadius: "10px", cursor: "pointer",
                  background: isActive ? "#C8172F" : "#fff",
                  color: isActive ? "#fff" : "#8A8A8A",
                  border: "1px solid #E0E0E0",
                  letterSpacing: "1px", flexShrink: 0,
                  opacity: isFutureDay ? 0.4 : 1,
                  pointerEvents: isFutureDay ? "none" : "auto",
                }}
              >
                <Typography fontSize={12}>{item.day}</Typography>
                <Typography fontWeight="bold">{item.date}</Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ p: 2 }}>
        {/* MONTHLY TOTAL BANNER */}
        {/* {totalDisplay && (
          <Box sx={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            background: "#fff", borderRadius: "10px", px: 2, py: 1.2, mb: 2,
            boxShadow: "0px 1px 4px rgba(0,0,0,0.07)",
          }}>
            <Typography fontSize={13} color="#666">Monthly Total</Typography>
            <Typography fontWeight={700} fontSize={15} color="#C8172F">{totalDisplay} Packs</Typography>
          </Box>
        )} */}

        <Typography variant="h6" fontWeight="bold" mb={2} sx={{ textAlign: "left", fontSize: "20px" }}>
          List of variants
        </Typography>

        <GroupedVariantList
          groupedData={groupedData}
          values={values}
          setValues={setValues}
          openGroup={openGroup}
          setOpenGroup={setOpenGroup}
        />

        {isLocked && (
          <Typography sx={{ textAlign: "center", color: "#999", fontSize: 13, mt: 1 }}>
            Previous month data can only be edited until the 7th of the current month.
          </Typography>
        )}

        <Box sx={{ display: "flex", gap: "10px" }}>
          <Button
            fullWidth
            onClick={isLocked ? () => navigate("/top-monthly-overview") : handleSubmit}
            sx={{
              mt: 1, color: "#fff", fontWeight: 500, padding: "0",
              background: "#C8172F", height: "44px", borderRadius: "10px",
              "&:hover": { background: "#C8172F", color: "#fff" },
            }}
          >
            {isLocked ? "View Monthly Overview" : "Submit Self Tracking"}
          </Button>
          <Button
            onClick={() => navigate(-1)}
            sx={{
              mt: "8px", textTransform: "none", color: "#C8172F", fontWeight: 600,
              background: "#fff", border: "1px solid #C8172F", height: "44px",
              width: "100px", borderRadius: "10px",
              "&:hover": { background: "#C8172F", color: "#fff" },
            }}
          >
            Back
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default SelfAuditFlow;
